import { object } from '@calendly/form/validation'
import { get, identity, isEmpty, isUndefined, omitBy, snakeCase } from 'lodash-es'

import { CUSTOM_RULE_ID } from 'components/availability/form/constants'
import {
  bookingRangeValuesToServerData,
  determineMembers,
  formatDurationValues,
  getBookingRangeValidationSchema,
  getDurationValidationSchema,
  getMinBookingTime,
  getNoticePeriodValidationSchema
} from 'components/event_type_edit'
import { getAvailabilityFromRule } from 'components/event_type_edit/utils/get_availability_from_rule'
import { panelLockNames, PANELS } from 'constants/event_type_editor_panels'
import {
  areAvailableHoursValid,
  getFormatAvailabilityValues,
  getFormatLocationValues,
  getLocationValidationSchema,
  getNameAndColorValidationSchema
} from 'features/event_type_editor'
import { isEtLocked } from 'services/event_type_locking'

export const BOOKED = 'booked'

export const panelKeys = [
  PANELS.duration,
  PANELS.location,
  PANELS.availability,
  PANELS.hosts
]

const createTransformer = (processor = identity) => {
  const transform = (value) => {
    if (Array.isArray(value)) {
      return value.map(transform)
    }

    if (typeof value === 'object' && value !== null) {
      const result = Object.fromEntries(
        Object.entries(value).map(
          ([key, nestedValue]) => [processor(key), transform(nestedValue)]
        ))

      Object.entries(Object.getOwnPropertyDescriptors(value)).forEach(([propertyKey, propertyValue]) => {
        if (propertyValue.enumerable) return

        Object.defineProperty(result, processor(propertyKey), propertyValue)
      })

      return result
    }

    return value
  }

  return transform
}

const transformSharedOverridesToSnakeCase = createTransformer(snakeCase)

export const mergeSharedAttributes = (eventType, shareOverride, formOverrides = {}) => {
  if (!eventType || isEmpty(eventType)) { return {} }
  if (!shareOverride) { return eventType }

  const {
    startDate,
    endDate,
    periodType,
    maxBookingTime,
    minBookingTime,
    duration,
    durationInMinutes,
    durationOptions,
    locationConfigurations,
    locationType,
    locationTypesCount,
    hideLocation,
    name,
  } = shareOverride

  const transformedShareOverride = transformSharedOverridesToSnakeCase(
    omitBy({
      startDate,
      endDate,
      periodType,
      maxBookingTime,
      minBookingTime,
      duration,
      durationInMinutes,
      durationOptions,
      locationConfigurations,
      locationType,
      locationTypesCount,
      hideLocation,
      name,
    }, isUndefined)
  )

  return { ...eventType, ...transformedShareOverride, ...formOverrides }
}

export const getShareOverrideAvailability = ({ eventType, shareOverride, defaultAvailabilityRules }) => {
  if (!eventType || isEmpty(eventType)) { return null }

  const isPersonalSolo = eventType.isUser && eventType.isSolo

  if (!isPersonalSolo) { return null }

  const members = determineMembers(eventType, defaultAvailabilityRules.timezone)
  const member = Object.values(members)[0]

  if (!shareOverride) {
    return {
      availableHours: member.availableHours,
      dateOverrides: member.dateOverrides,
      timezone: member.timezone,
      customRuleId: CUSTOM_RULE_ID,
      usingCustomHours: true,
      usingSecondarySchedule: false,
      creation: true
    }
  }

  const { availabilityRule } = shareOverride

  const availability = availabilityRule ? {
    id: availabilityRule.id,
    customRuleId: availabilityRule.id,
    ...getAvailabilityFromRule(availabilityRule)
  } : {
    availableHours: member.availableHours,
    dateOverrides: member.dateOverrides,
    timezone: member.timezone,
    customRuleId: CUSTOM_RULE_ID,
  }

  return {
    ...(shareOverride.id ? { id: shareOverride.id } : {}),
    ...availability,
    usingCustomHours: true,
    usingSecondarySchedule: false
  }
}

export const getEventTypeLocks = (eventType) => {
  const { isSolo, isUser } = eventType

  const locks = {
    [PANELS.nameAndColor]: isEtLocked(eventType, panelLockNames[PANELS.nameAndColor]),
    [PANELS.duration]: isEtLocked(eventType, panelLockNames[PANELS.duration]),
    [PANELS.location]: false,
    [PANELS.availability]: isEtLocked(eventType, panelLockNames[PANELS.availability]),
    [PANELS.hosts]: false
  }

  return (isSolo && isUser)
    ? locks
    : {
      [PANELS.nameAndColor]: true,
      [PANELS.duration]: true,
      [PANELS.location]: true,
      [PANELS.availability]: true,
      [PANELS.hosts]: true
    }
}

export const getFormatValuesToServerData = (eventType, isSULCreation = false) => (values) => {
  const locks = getEventTypeLocks(eventType)

  const formatAvailabilityValues = getFormatAvailabilityValues(eventType)
  const formatLocationValues = getFormatLocationValues(eventType)

  const durationAttributes = formatDurationValues(values)
  const { shareOverrideAvailability } = formatAvailabilityValues(values)
  const availabilityAttributes = !locks.availability
    ? shareOverrideAvailability
    : {
      rules: get(eventType, 'active_availability_data.rules'),
      timezone: get(eventType, 'active_availability_data.timezone'),
    }
  const {
    hide_location: hideLocation,
    location_configurations_attributes: locationConfigurations
  } = formatLocationValues(values)

  const bookingRange = bookingRangeValuesToServerData(values)
  const bookingRangeAttributes = {
    period_type: bookingRange.period_type,
    start_date: bookingRange.start_date || undefined,
    end_date: bookingRange.end_date || undefined,
    max_booking_time: bookingRange.max_booking_time || undefined,
    min_booking_time: getMinBookingTime(values.minBookingTime, values.minBookingTimeType)
  }

  const cleanedLocationConfigurations = locationConfigurations
    .filter((locationConfiguration) => !(isSULCreation && locationConfiguration._destroy))
    .map((locationConfiguration) => Object.fromEntries(
      Object.entries(locationConfiguration).filter(([_key, value]) => value !== null && value !== '')
    ))

  return {
    ...durationAttributes,
    ...bookingRangeAttributes,
    event_type_id: eventType.id,
    availability_rule: (!availabilityAttributes.rules && !availabilityAttributes.timezone) ? undefined : availabilityAttributes,
    location_configurations: cleanedLocationConfigurations,
    hide_location: hideLocation,
    contact_uuid: values.contact?.uuid || undefined,
    name: values.name,
  }
}

const getAvailableHoursValidationSchema = () => object({
  shareOverrideAvailability: object().test('validateAvailableHours', '', (value) => {
    if (!value?.availableHours || !value?.usingCustomHours) return true

    return areAvailableHoursValid(value.availableHours)
  })
})

const getAvailabilityValidationSchema = () => getAvailableHoursValidationSchema()
  .concat(getBookingRangeValidationSchema())
  .concat(getNoticePeriodValidationSchema())

export const getValidationSchema = (eventType) => {
  const locks = getEventTypeLocks(eventType)

  return object({})
    .concat(!locks[PANELS.nameAndColor] ? getNameAndColorValidationSchema() : object({}))
    .concat(!locks[PANELS.duration] ? getDurationValidationSchema() : object({}))
    .concat(getLocationValidationSchema(eventType))
    .concat(!locks[PANELS.availability] ? getAvailabilityValidationSchema() : object({}))
}
